/**
 * Price Rules utilities.
 *
 * Provides dynamic pricing adjustments stored in listing.attributes.publicData.priceRules[].
 *
 * Rule types:
 *   weekend     — applies to all weekend nights (Sat/Sun)
 *   lastMinute  — applies when booking is within N days
 *   season      — applies to bookings starting in specified months
 *   custom      — applies to a specific date range
 *
 * Each rule: { type, adjust: number, unit: 'percent' | 'fixed', ...typeSpecificFields }
 *
 * Example publicData.priceRules:
 * [
 *   { type: 'weekend', adjust: 20, unit: 'percent' },
 *   { type: 'lastMinute', days: 7, adjust: -15, unit: 'percent' },
 *   { type: 'season', months: [6, 7, 8], adjust: 30, unit: 'percent' },
 *   { type: 'custom', startDate: '2026-12-24', endDate: '2026-12-26', adjust: 50, unit: 'percent' }
 * ]
 *
 * Integration:
 *   1. Operator adds priceRules to listing via Console or Integration API
 *   2. In lineItems.js — call applyPriceRules(baseUnitPrice, bookingStart, rules)
 *   3. Show adjusted price in OrderPanel with PriceRulesBadge
 */

/**
 * Check if a date is a weekend day (Saturday or Sunday).
 */
export const isWeekend = date => {
  const d = new Date(date);
  const day = d.getUTCDay();
  return day === 0 || day === 6;
};

/**
 * Calculate days until a date from today.
 */
export const daysUntil = date => {
  const diff = new Date(date).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

/**
 * Check if a rule applies for the given booking start date.
 * @param {Object} rule
 * @param {Date|string} bookingStart
 * @returns {boolean}
 */
export const ruleApplies = (rule, bookingStart) => {
  if (!rule || !bookingStart) return false;
  const d = new Date(bookingStart);

  switch (rule.type) {
    case 'weekend':
      return isWeekend(d);

    case 'lastMinute': {
      const days = typeof rule.days === 'number' ? rule.days : 7;
      const daysDiff = daysUntil(d);
      return daysDiff >= 0 && daysDiff <= days;
    }

    case 'season': {
      if (!Array.isArray(rule.months)) return false;
      const month = d.getUTCMonth() + 1; // 1-12
      return rule.months.includes(month);
    }

    case 'custom': {
      if (!rule.startDate || !rule.endDate) return false;
      const start = new Date(rule.startDate).getTime();
      const end = new Date(rule.endDate).getTime();
      const ts = d.getTime();
      return ts >= start && ts <= end;
    }

    default:
      return false;
  }
};

/**
 * Apply price adjustment from a single rule to a base amount (in cents).
 * @param {number} baseAmount — price in cents
 * @param {Object} rule — { adjust, unit }
 * @returns {number} — adjusted amount in cents
 */
export const applyRule = (baseAmount, rule) => {
  if (rule.unit === 'percent') {
    return Math.round(baseAmount * (1 + rule.adjust / 100));
  }
  if (rule.unit === 'fixed') {
    return baseAmount + rule.adjust; // adjust in cents
  }
  return baseAmount;
};

/**
 * Apply all matching price rules to a base unit price.
 * Rules are applied in order — first matching rule wins.
 * Set PRICE_RULES_STACK=true to apply all matching rules instead.
 *
 * @param {number} baseAmount — unit price in cents
 * @param {Date|string} bookingStart
 * @param {Object[]} rules — from listing.attributes.publicData.priceRules
 * @param {Object} options
 * @param {boolean} [options.stack] — apply all matching rules (default: false = first wins)
 * @returns {{ amount: number, appliedRules: Object[] }}
 */
export const applyPriceRules = (baseAmount, bookingStart, rules, { stack = false } = {}) => {
  if (!Array.isArray(rules) || rules.length === 0) {
    return { amount: baseAmount, appliedRules: [] };
  }

  const matching = rules.filter(r => ruleApplies(r, bookingStart));
  if (matching.length === 0) return { amount: baseAmount, appliedRules: [] };

  const toApply = stack ? matching : [matching[0]];
  let amount = baseAmount;
  toApply.forEach(rule => {
    amount = applyRule(amount, rule);
  });

  return { amount, appliedRules: toApply };
};

/**
 * Get price rules from a listing entity.
 */
export const getPriceRules = listing =>
  listing?.attributes?.publicData?.priceRules || [];
